import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';

import { TFunction } from 'i18next';

interface ChartData {
  date_time: string[];
  caught_pests: number[];
  minMaxSelection: number[];
  caught_empty: number[];
  enter: string[];
}

export const timeLineChart = (chartData: ChartData, t: TFunction, isBarChart: boolean = false, isRat: boolean) => {
  const type = isBarChart ? 'bar' : 'area';
  const xaxisType = isBarChart ? 'category' : 'datetime';

  const formattedPestsData = isBarChart
    ? chartData.caught_pests || []
    : (chartData.date_time || []).map((date, index) => [new Date(date).getTime(), chartData.caught_pests[index] || 0]);

  const formattedEmptyData = isBarChart
    ? chartData.caught_empty || []
    : (chartData.date_time || []).map((date, index) => [new Date(date).getTime(), chartData.caught_empty[index] || 0]);

  const chartOptions: ApexOptions = {
    series: [
      {
        name: isRat ? t('MCTDetail.timeline.value_rat') : t('MCTDetail.timeline.value'),
        type: type,
        data: formattedPestsData as any,
      },
      {
        name: t('MCTDetail.timeline.caught_empty'),
        type: type,
        data: formattedEmptyData as any,
        color: '#ffe529',
      },
    ],
    chart: {
      height: 350,
      type: type,
      id: 'timeline-chart',
      redrawOnWindowResize: true,
      redrawOnParentResize: true,
      zoom: {
        enabled: true,
        type: 'x',
      },
      toolbar: {
        show: true,
        autoSelected: 'zoom',
      },
    },
    markers: {
      size: isBarChart ? 0 : 3,
      shape: 'circle',
    },
    stroke: {
      show: !isBarChart,
      width: isBarChart ? 0 : [3, 3],
      curve: ['smooth', 'smooth', 'stepline'],
    },
    fill: {
      opacity: isBarChart ? 1 : 0.6,
      type: isBarChart ? 'solid' : 'gradient',
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: '70%',
        barHeight: '100%',
        borderRadiusApplication: 'end',
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      custom: (opts: any) => {
        const { series, seriesIndex, w, dataPointIndex } = opts;
        let dateStr = '';
        let enterStr = '';

        const rawDate = chartData.date_time[dataPointIndex];
        if (rawDate) {
          dateStr = dayjs(rawDate).format('DD-MM-YYYY HH:mm:ss');
        }

        if (typeof dataPointIndex !== 'undefined' && chartData.enter && chartData.enter[dataPointIndex]) {
          const val = chartData.enter[dataPointIndex];
          const strVal = val.toString();
          const isLeft = val === 'L' || strVal === '1';
          const isRight = val === 'R' || strVal === '2';
          const displayChar = isLeft ? 'L' : isRight ? 'R' : val;

          if (isLeft) {
            enterStr = `<div class="d-flex align-items-center"><i class="ri-arrow-left-line"></i><div>${displayChar}</div></div>`;
          } else if (isRight) {
            enterStr = `<div class="d-flex align-items-center"><div>${displayChar}</div><i class="ri-arrow-right-line"></i></div>`;
          } else {
            enterStr = `<div>${displayChar}</div>`;
          }
        }

        const seriesName = w.globals.seriesNames[seriesIndex];
        const value = series[seriesIndex][dataPointIndex];
        return `
        <div class="p-2">
          <div class="d-flex justify-content-between gap-1 pb-2 border-bottom">
          <div class="d-flex">${dateStr}</div>
          ${!isRat ? `<div class="d-flex">${enterStr}</div>` : ''}
          </div>
          <div class="d-flex justify-content-center align-items-center gap-1 mt-2">
          <b>${seriesName}:</b> ${value}<br/>
          </div>
        </div>
        `;
      },
    },
    xaxis: {
      type: xaxisType,
      tickAmount: 6,
      ...(isBarChart ? { categories: chartData.date_time || [] } : {}),
      title: {
        text: t('MCTDetail.timeline.x_axis'),
        style: { fontSize: '12px' },
        offsetY: 40,
      },
      labels: {
        formatter: val => {
          if (!val) return '';
          const d = dayjs(val);
          return d.isValid() ? [String(d.format('DD-MM-YYYY')), String(d.format('HH:mm:ss'))] : val;
        },
        rotate: -90,
        rotateAlways: true,
        hideOverlappingLabels: true,
        offsetY: 10,
      },
    },
    yaxis: {
      title: {
        text: !isRat ? t('MCTDetail.timeline.y_axis') : t('MCTDetail.timeline.y_axis_2'),
        style: { fontSize: '12px' },
        offsetX: 3,
        offsetY: 50,
      },
      min: 0,
      forceNiceScale: true,
      labels: {
        minWidth: 10,
        formatter: val => {
          return val.toFixed(0);
        },
      },
    },
    legend: {
      show: true,
      position: 'bottom',
      offsetY: 0,
      markers: {
        shape: 'square',
      },
    },

    grid: {
      padding: {
        bottom: 60,
      },
    },
    colors: ['#6A37D4', '#ffe529'],
  };

  return chartOptions;
};

export const brushChartOptions = (chartData: ChartData) => {
  const formattedPestsData = (chartData.date_time || []).map((date, index) => {
    return [new Date(date).getTime(), chartData.caught_pests[index] || 0];
  });

  const brushOptions: ApexOptions = {
    series: [
      {
        name: 'Bait quantity',
        type: 'line',
        data: formattedPestsData,
        color: '#6A37D4',
      },
    ],
    chart: {
      id: 'brush-chart',
      height: 100,
      type: 'line',
      stacked: false,
      brush: {
        enabled: true,
        target: 'timeline-chart',
      },
      selection: {
        enabled: true,
        xaxis: {
          min: chartData.minMaxSelection[0],
          max: chartData.minMaxSelection[1],
        },
      },
    },
    xaxis: {
      type: 'datetime',
      offsetY: 1,
      labels: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: {
      tickAmount: 2,
      labels: {
        formatter: val => {
          return val.toFixed(0);
        },
      },
    },
    legend: { show: false },
    dataLabels: { enabled: false },
  };

  return brushOptions;
};
